import React, { useEffect } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Paper
} from '@mui/material';
import {
  Unarchive as UnarchiveIcon,
  Visibility as ViewIcon,
  Archive as ArchiveIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStudents, setFilterValues, unarchiveStudent } from '../slices/studentsSlice';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';
import { exportToPrintWindow } from '../utils/exportUtils';

const MotionPaper = motion(Paper);

const ArchivedStudentsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { students, filterValues } = useSelector((state) => state.students);
  const archivedStudents = students?.filter((s) => s.is_archived);
  const { showToast } = useToast();

  useEffect(() => {
    dispatch(fetchStudents({ is_archived: true }));
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilterValues({ ...filterValues, [field]: value }));
  };

  const handleUnarchive = (id) => {
    dispatch(unarchiveStudent(id))
      .then(() => {
        dispatch(fetchStudents()); // ⬅️ Refresh list
        showToast('تم استرجاع الطالب من الأرشيف', 'success');
      })
      .catch(() => showToast('حدث خطأ أثناء الاسترجاع', 'error'));
  };

  const handleExport = () => {
    // هنا تضع كود التصدير الفعلي باستخدام مكتبة xlsx
    alert('تصدير إلى Excel غير مفعل حالياً');
  };

  const handlePrint = () => {
    const columns = [
      { header: 'رقم الهوية', key: 'student_id' },
      { header: 'الاسم الكامل', key: 'fullName' },
    ];
    const data = archivedStudents.map(s => ({
      student_id: s.student_id,
      fullName: `${s.first_name || ''} ${s.second_name || ''}`.trim(),
    }));
    exportToPrintWindow(data, columns, 'الطلاب المؤرشفين');
  };

  const columns = [
    {
      field: 'student_id',
      headerName: 'رقم الهوية',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">رقم الهوية</Typography>
          <TextField
            variant="standard"
            value={filterValues.id || ''}
            onChange={(e) => handleFilterChange('id', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '0.75rem', textAlign: 'center' },
            }}
          />
        </Box>
      )
    },
    {
      field: 'fullName',
      headerName: 'الاسم الكامل',
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <Typography>
          {`${params.row.first_name || ''} ${params.row.second_name || ''}`}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'إجراءات',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={1}>
          <Button onClick={() => navigate(`/students/view/${params.row.id}`)}>
            <ViewIcon fontSize="small" />
          </Button>
          <Button
            onClick={() => handleUnarchive(params.row.id)}
            color="success"
            variant="contained"
            startIcon={<UnarchiveIcon />}
          >
            استرجاع
          </Button>
        </Stack>
      )
    }
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArchiveIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                أرشيف الطلاب
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                عرض واسترجاع الطلاب المؤرشفين
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Export Actions */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصدير القائمة
          </Typography>

          <Stack useFlexGap
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
            </Button>
          </Stack>
        </MotionPaper>

        {/* Table */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <DynamicDataGrid
            columns={columns}
            rows={archivedStudents}
          />
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default ArchivedStudentsPage;
